import { useTranslate } from 'react-admin';
import { Alert, Box, Button, Card, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';
import type { ConfigEntry } from 'src/pages/admin/services/vaultParamsService';
import { PermissionButton } from 'src/routes/components/permission-button';

import { VAULT_PARAMS_NS as NS, VAULT_PARAMS_PERMISSION_RESOURCE } from './constants';
import { FieldSpec } from './fields';
import { fieldUnits, formatValue, groupFields } from './helpers';
import { ParamsEditDialog } from './ParamsEditDialog';
import { ConfirmChangesDialog } from './ConfirmChangesDialog';
import { ParamsHistoryButton } from './ParamsHistoryButton';
import type { ParamsEditor } from './useVaultParams';

interface ParamsCardProps<T> {
  title: string;
  subtitle: string;
  modalTitle: string;
  fields: FieldSpec<T>[];
  entry: ConfigEntry<T>;
  defaults: T | null;
  editor: ParamsEditor<T>;
  /** Audit record id of this key (`core` / `risk`), for the change-history dialog. */
  historyRecordId: 'core' | 'risk';
}

export const ParamsCard = <T,>({
  title,
  subtitle,
  modalTitle,
  fields,
  entry,
  defaults,
  editor,
  historyRecordId,
}: ParamsCardProps<T>) => {
  const translate = useTranslate();
  // What the engine actually runs on: the stored value, or its default.
  const effective = entry.value ?? defaults;
  const units = fieldUnits(translate);

  return (
    <Card sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
          >
            <Typography variant="h6">{translate(title)}</Typography>
            {!entry.isSet && <Chip size="small" label={translate(`${NS}.notSet`)} />}
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {translate(subtitle)}
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          <ParamsHistoryButton recordId={historyRecordId} cardTitle={title} />
          <PermissionButton resource={VAULT_PARAMS_PERMISSION_RESOURCE} action="update">
            {effective ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Iconify icon="solar:pen-bold" />}
                onClick={editor.openDialog}
                sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}
              >
                {translate(`${NS}.edit`)}
              </Button>
            ) : (
              // Neither a stored value nor a default came back — editing would open
              // an empty form. Tooltip needs a span wrapper since the button itself
              // is disabled and would otherwise swallow the hover/focus events.
              <Tooltip title={translate(`${NS}.editUnavailable`)}>
                <span style={{ display: 'inline-flex', alignSelf: 'stretch' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Iconify icon="solar:pen-bold" />}
                    disabled
                    sx={{ flexShrink: 0, width: '100%', alignSelf: { xs: 'stretch', sm: 'auto' } }}
                  >
                    {translate(`${NS}.edit`)}
                  </Button>
                </span>
              </Tooltip>
            )}
          </PermissionButton>
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {editor.notice && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate(
            editor.notice === 'conflict' ? `${NS}.conflictBanner` : `${NS}.maybeAppliedBanner`
          )}
        </Alert>
      )}

      {/* One section per field group (risk: trading / redemption / venue clock);
          core has no groups and renders as a single untitled grid. */}
      <Stack spacing={3}>
        {groupFields(fields).map(({ group, fields: sectionFields }) => (
          <Box key={group ?? 'ungrouped'}>
            {group && (
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {translate(group)}
              </Typography>
            )}
            <Box
              component="dl"
              sx={{
                m: 0,
                display: 'grid',
                gap: 3,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
              }}
            >
              {sectionFields.map(({ key, kind, label, enforced }) => (
                // Label, value, then the not-enforced chip: the chip sits under the value
                // so values in one grid row line up whatever the label length.
                <Box key={key} sx={{ minWidth: 0 }}>
                  <Typography component="dt" variant="caption" sx={{ color: 'text.secondary' }}>
                    {translate(label)}
                  </Typography>
                  <Typography
                    component="dd"
                    variant="subtitle1"
                    sx={{ m: 0, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}
                  >
                    {effective ? formatValue(kind, effective[key], units) : '—'}
                  </Typography>
                  {!enforced && (
                    <Chip
                      component="dd"
                      size="small"
                      variant="outlined"
                      label={translate(`${NS}.notEnforced`)}
                      sx={{
                        m: 0,
                        mt: 0.5,
                        maxWidth: '100%',
                        height: 'auto',
                        color: 'text.secondary',
                        '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>

      <ParamsEditDialog title={modalTitle} fields={fields} editor={editor} />
      <ConfirmChangesDialog editor={editor} />
    </Card>
  );
};
